/**
 * Gates editor change-reporting so only genuine user edits reach the parent.
 *
 * Milkdown fires programmatic transactions at mount (cursor placement, the
 * trailing-paragraph plugin, jinja-token normalization) whose re-serialized
 * markdown rarely matches the raw server baseline byte-for-byte. Reporting
 * those would falsely arm Save/Cancel on load and re-arm them right after a
 * Cancel remount. The gate stays closed until a real user-input intent calls
 * `markEdited`, and closes permanently once `markDestroyed` runs so a late
 * debounced flush can't write stale markdown after teardown.
 *
 * Kept as a pure helper (no Milkdown/DOM refs) so the armed/destroyed
 * semantics are unit-testable without a real editor or browser.
 */
export type EditGate = {
  /** Arm the gate — call on the first real user gesture/input. */
  markEdited: () => void
  /** Close the gate permanently — call on teardown. */
  markDestroyed: () => void
  /** Emit the current markdown, but only while armed and not destroyed. */
  flushIfEdited: () => void
}

export function createEditGate({
  getMarkdown,
  emit,
}: {
  /** Serialize the current document; returns null if unavailable (e.g. torn down). */
  getMarkdown: () => string | null
  /** Receives the markdown when a flush passes the gate. */
  emit: (markdown: string) => void
}): EditGate {
  let userHasEdited = false
  let destroyed = false

  return {
    markEdited: () => {
      userHasEdited = true
    },
    markDestroyed: () => {
      destroyed = true
    },
    flushIfEdited: () => {
      if (!userHasEdited || destroyed) return
      const md = getMarkdown()
      if (typeof md === 'string') emit(md)
    },
  }
}
